import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, HeartHandshake, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import DisclosureBanner from "../components/DisclosureBanner";

const MSG = {
  interested: {
    title: "Thank you — your interest is confirmed",
    body: "Thank you for confirming your continued interest in joining Integrity Delivered, Inc. Your response has been recorded. Our Human Resources team will continue reviewing your application and will contact you regarding the next step if your qualifications meet our current hiring needs.",
  },
  withdraw: {
    title: "Your application has been withdrawn",
    body: "Thank you for taking the time to learn about the position and for letting us know. We appreciate your interest in Integrity Delivered, Inc. and wish you the very best. Should your circumstances change, you are welcome to apply again in the future.",
  },
};

export default function JobPreviewResponse() {
  const { token, choice } = useParams();
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/job-preview/${token}`);
        setInfo(data);
        if (data.response) { setState("already"); return; }
        if (choice !== "interested" && choice !== "withdraw") { setState("invalid"); return; }
        await api.post(`/job-preview/${token}/respond`, { choice });
        setState("done");
      } catch (e) { setError(formatApiError(e)); setState("invalid"); }
    })();
  }, [token, choice]);

  const final = state === "already" ? info?.response : choice;
  const msg = MSG[final];

  return (
    <div>
      <DisclosureBanner />
      <section className="py-20 min-h-[60vh] flex items-center">
        <div className="max-w-xl mx-auto px-4 w-full rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center">
          <img src="/logo.png" alt="Integrity Delivered" className="h-16 w-auto mx-auto mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" />
          {state === "loading" && <div data-testid="preview-loading" className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>}
          {state === "invalid" && (
            <div data-testid="preview-invalid">
              <h1 className="text-2xl font-extrabold uppercase display text-white">Link not valid</h1>
              <p className="mt-3 text-sm text-slate-400">{error || "This link is not valid. Please use the buttons in your email or contact Human Resources."}</p>
            </div>
          )}
          {(state === "done" || state === "already") && msg && (
            <div data-testid={`preview-${final}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${final === "interested" ? "bg-emerald-500/15 border border-emerald-500/40" : "bg-slate-800 border border-slate-700"}`}>
                {final === "interested" ? <CheckCircle2 className="w-8 h-8 text-emerald-400" /> : <HeartHandshake className="w-8 h-8 text-slate-300" />}
              </div>
              <h1 className="mt-6 text-2xl sm:text-3xl font-extrabold uppercase display text-white">{msg.title}</h1>
              {info?.first_name && <p className="mt-2 text-amber-400 text-sm font-semibold">Hi {info.first_name}</p>}
              <p data-testid="preview-message" className="mt-4 text-slate-300 leading-relaxed">{msg.body}</p>
              {state === "already" && <p data-testid="preview-already-note" className="mt-3 text-xs text-slate-500">We already have your response on file from {new Date(info.response_at).toLocaleString()}.</p>}
              <div className="mt-6 text-xs text-slate-500 flex items-center justify-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Integrity Delivered, Inc. · Human Resources</div>
              <Link to="/"><Button variant="outline" className="mt-6 border-slate-700 text-slate-200">Back to home</Button></Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
